import React, { useCallback, useMemo, useEffect } from "react";
import { connect } from "react-redux";
import FormField from '../../../../../dynamicFormBuilder/FormField';
import { PrimaryLayout } from '../Layout';
import { relaxRequiredForProfileDefaults } from '../../../../../common/profileDefaults';
import { DisableEnableControls, loadLookupFromStaticDataSource, SetControlValue } from '../../../../../common/Utils';

function PrimaryTab(props) {
    var currentUser = props.oidc?.user;
    const DYNAMICFORMSETTING = props.oidc?.user?.profile?.DYNAMICFORMSETTING;
    const isSaved = props?.formFields?.ROWID && props.formFields.ROWID !== "00000000-0000-0000-0000-000000000000";

    // The Utils helpers skip a ref whose widget has been disposed (instance() returns undefined) -
    // which happens when the TabPanel re-renders its tabs while these effects are still queued.
    const loadLookupClient = useCallback((name, dataSource, valueExpr = 'id', displayExpr = 'text') => {
        loadLookupFromStaticDataSource(name, dataSource, props.formFields, props.formRefs, valueExpr, displayExpr);
    }, [props.formRefs, props.formFields]);

    const setFieldDisabled = useCallback((name, disabled) => {
        DisableEnableControls(name, disabled, props.formRefs);
    }, [props.formRefs]);

    const applyItemClassRule = useCallback((scheduleType) => {
        const isItemSchedule = scheduleType === 'I';
        setFieldDisabled('ITEMCLASS', !isItemSchedule);
        setFieldDisabled('ITEMSUBCLASS', !isItemSchedule);

        if (!isItemSchedule) {
            // Clear item class and item sub class values
            SetControlValue('ITEMCLASS', '', props.formRefs);
            SetControlValue('ITEMSUBCLASS', '', props.formRefs);
            props.setformFields(prev => ({
                ...prev,
                ITEMCLASS: '',
                ITEMSUBCLASS: ''
            }));
        }
    }, [props.formRefs, props.setformFields, setFieldDisabled]);

    const finalLayout = useMemo(() => {
        // A profile template has no Schedule Code of its own - uWMD0017.SetParameters clears
        // NonBlankFields, and RateScheduleHeaderService applies the matching server-side exemption.
        return props.isProfileDefaults ? relaxRequiredForProfileDefaults(PrimaryLayout) : PrimaryLayout;
    }, [isSaved, props.isProfileDefaults]);

    const scheduleTypeOptions = [
        { id: 'I', text: 'Item Schedule' },
        { id: 'T', text: 'Transaction Schedule' },
        { id: 'S', text: 'Storage Transaction Schedule' }
    ];

    const handleSelectionChange = useCallback((name, value, e) => {
        props.handleFieldChange(name, value, e);

        if (name === 'SCHEDULETYPE') {
            setTimeout(() => {
                applyItemClassRule(value);
            }, 50);
        }
    }, [props.handleFieldChange, applyItemClassRule]);

    const applyEditModeRule = useCallback(() => {
        // The profile is fixed once the record exists, and always on a profile template
        // (SetParameters: ab_EnableControl(edtP_PROFILE, False)).
        if (isSaved || props.isProfileDefaults) {
            setFieldDisabled('R_PROFILECODE', true);
        }
        // Schedule Type is fixed once a real schedule exists, but stays editable on a profile
        // template: uWMD0017 qeWMRateScheduleHeaderAfterScroll disables it for an existing record and
        // then re-enables it when RecordType = 'P'.
        if (isSaved) {
            setFieldDisabled('SCHEDULETYPE', !props.isProfileDefaults);
        }
    }, [isSaved, props.isProfileDefaults, setFieldDisabled]);

    const handlePrimaryFormLoad = useCallback(() => {
        setTimeout(() => {
            loadLookupClient("SCHEDULETYPE", scheduleTypeOptions);
            applyItemClassRule(props.formFields?.SCHEDULETYPE);
            applyEditModeRule();
        }, 100);
    }, [loadLookupClient, applyItemClassRule, applyEditModeRule, props.formFields?.SCHEDULETYPE]);

    useEffect(() => {
        loadLookupClient("SCHEDULETYPE", scheduleTypeOptions);
    }, [loadLookupClient, props.formFields?.SCHEDULETYPE]);

    // Apply the business rule whenever SCHEDULETYPE changes
    useEffect(() => {
        applyItemClassRule(props.formFields?.SCHEDULETYPE);
    }, [props.formFields?.SCHEDULETYPE, applyItemClassRule]);

    // Apply edit mode rule when record is saved/loaded
    useEffect(() => {
        applyEditModeRule();
    }, [isSaved, applyEditModeRule]);

    return (
        <div>
            <FormField
                currrentUser={currentUser}
                layoutId='14E6CC50-9961-4163-A865-FCBC469914E4'
                layoutName='WMRateScheduleCodePrimaryForm'
                defaultFormSchema={finalLayout}
                formFields={props.formFields}
                setformFields={props.setformFields}
                handleFieldValueChange={handleSelectionChange}
                DYNAMICFORMSETTING={DYNAMICFORMSETTING}
                formRefs={props.formRefs}
                onFormLoad={handlePrimaryFormLoad}
            />
        </div>
    );
}

function mapStateToProps(state) {
    return {
        oidc: state.oidc
    };
}

export default connect(mapStateToProps)(PrimaryTab);

